import React, { useState, useEffect, useMemo } from "react";
import {
  subscribeToUserCollection,
  addUserDoc,
  updateUserDoc,
  deleteUserDoc,
} from "../utils/userDataHelper";
import { convertQuantity } from '../utils/workflowPlanning';
import AddIngredient from "./AddIngredient";
import Breadcrumbs from "../Components/Breadcrumbs";
import Footer from "../Components/Footer";
import "./labcss/ingredient-page.css";
import useCurrencyTools from "../hooks/useCurrencyTools";

// enums and helpers for oil metadata
const OIL_GROUPS = ["conditioning", "cleansing", "butter", "lather_booster"];
const LATHER_TYPES = ["low", "creamy", "bubbly"];
const TRACE_SPEEDS = ["slow", "medium", "fast"];
const isOil = (ing = {}) => {
  const t = (ing.type || ing.category || "").toLowerCase();
  return t.includes("oil");
};

const num = (v, def = 0) => {
  const n = typeof v === "number" ? v : parseFloat(v);
  return Number.isFinite(n) ? n : def;
};

// A calm category tile instead of third-party images (which were unreliable and sent
// ingredient names to outside sites).

export default function Inventory() {
  const [ingredients, setIngredients] = useState([]);
  const [editingIdx, setEditingIdx] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const { preferredCurrency, formatMoney, convertAndFormat } = useCurrencyTools();

  const LOW_STOCK_THRESHOLD_G = 100; // grams

  // Fetch ingredients from user's collection on mount
  useEffect(() => {
    let unsubscribe = () => {};
    
    try {
      unsubscribe = subscribeToUserCollection(
        'ingredients',
        (loadedIngredients) => {
          // Map 'id' to 'firebaseId' for compatibility
          const mappedIngredients = loadedIngredients.map(ing => ({
            ...ing,
            firebaseId: ing.id,
          }));
          setIngredients(mappedIngredients);
        }
      );
    } catch (error) {
      console.error("Error setting up ingredients subscription:", error);
    }
    
    return () => unsubscribe();
  }, []);


  // Get unique categories for filter dropdown
  const categories = useMemo(() => {
    const cats = new Set();
    ingredients.forEach(ing => {
      cats.add((ing.category || ing.type || 'other').toLowerCase());
    });
    return Array.from(cats).sort();
  }, [ingredients]);

  // Filtered + sorted ingredients
  const filteredIngredients = useMemo(() => {
    let list = [...ingredients];

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(ing =>
        (ing.name || '').toLowerCase().includes(q) ||
        (ing.key || '').toLowerCase().includes(q) ||
        (ing.supplier || '').toLowerCase().includes(q)
      );
    }

    // Category filter
    if (filterCategory !== 'all') {
      list = list.filter(ing =>
        (ing.category || ing.type || 'other').toLowerCase() === filterCategory
      );
    }

    // Sort
    list.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        case 'stock':
          return num(a.stock, 0) - num(b.stock, 0);
        case 'expiry': {
          const ea = a.expiryDate ? new Date(a.expiryDate).getTime() : Infinity;
          const eb = b.expiryDate ? new Date(b.expiryDate).getTime() : Infinity;
          return ea - eb;
        }
        case 'cost':
          return num(b.costPerUnit, 0) - num(a.costPerUnit, 0);
        default:
          return 0;
      }
    });

    return list;
  }, [ingredients, searchQuery, filterCategory, sortBy]);

  // Group filtered ingredients by category
  const filteredGrouped = useMemo(() => {
    const groups = {};
    filteredIngredients.forEach((ing) => {
      const cat = (ing.category || ing.type || "other").toLowerCase();
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(ing);
    });
    return groups;
  }, [filteredIngredients]);

  // Check if ingredient is low stock
  const isLowStock = (ing) => {
    const stock = num(ing.stock, -1);
    if (stock < 0) return false;
    const unit = (ing.unit || 'g').toLowerCase();
    let stockInGrams = stock;
    if (unit === 'kg') stockInGrams = stock * 1000;
    else if (['l', 'ml'].includes(unit)) {
      if (!(Number(ing.densityGPerMl) > 0)) return stock === 0;
      stockInGrams = convertQuantity(stock, unit, 'g', ing.densityGPerMl);
    }
    return stockInGrams < LOW_STOCK_THRESHOLD_G && stockInGrams >= 0;
  };

  const handleAddIngredient = async (ing) => {
    const normalized = { ...ing, currencyCode: preferredCurrency };
    if (isOil(normalized)) {
      normalized.recommendedMaxPct = num(normalized.recommendedMaxPct, 70);
      normalized.faceMaxPct = num(normalized.faceMaxPct, 100);
      normalized.absoluteMaxPct = num(normalized.absoluteMaxPct, 100);
      const og = String(normalized.oilGroup || "").toLowerCase();
      normalized.oilGroup = OIL_GROUPS.includes(og) ? og : "conditioning";
      const lt = String(normalized.latherType || "").toLowerCase();
      normalized.latherType = LATHER_TYPES.includes(lt) ? lt : "creamy";
      const ts = String(normalized.traceSpeed || "").toLowerCase();
      normalized.traceSpeed = TRACE_SPEEDS.includes(ts) ? ts : "medium";
    }
    try {
      await addUserDoc('ingredients', normalized);
    } catch (error) {
      console.error('Error adding ingredient:', error);
      throw error;
    }
  };

  const handleEdit = (idx) => {
    setEditingIdx(idx);
    setEditForm({ ...ingredients[idx] });
  };

  const handleSaveEdit = async (idx) => {
    if ([editForm.stock, editForm.costPerUnit].some((value) => !Number.isFinite(Number(value)) || Number(value) < 0)) { alert('Stock and cost must be non-negative numbers.'); return; }
    if (editForm.densityGPerMl != null && editForm.densityGPerMl !== '' && !(Number(editForm.densityGPerMl) > 0)) { alert('Density must be a positive number in g/ml.'); return; }
    if (editForm.sap != null && editForm.sap !== '' && !(Number(editForm.sap) >= 0.05 && Number(editForm.sap) <= 0.3)) { alert('Enter the NaOH SAP value as a decimal between about 0.06 and 0.28 (olive oil is 0.134). If your supplier gives mg KOH/g, such as 190, divide by 1403.'); return; }
    try {
      const ingredient = ingredients[idx];
      if (ingredient.firebaseId) {
        await updateUserDoc('ingredients', ingredient.firebaseId, editForm);
      }
      setEditingIdx(null);
      setEditForm({});
    } catch (error) {
      console.error('Error updating ingredient:', error);
      alert('Failed to update ingredient');
    }
  };

  const handleCancelEdit = () => {
    setEditingIdx(null);
    setEditForm({});
  };

  const handleDelete = async (idx) => {
    if (window.confirm('Delete this ingredient?')) {
      try {
        const ingredient = ingredients[idx];
        if (ingredient.firebaseId) {
          await deleteUserDoc('ingredients', ingredient.firebaseId);
        }
      } catch (error) {
        console.error('Error deleting ingredient:', error);
        alert('Failed to delete ingredient');
      }
    }
  };

  return (
    <>
      <div className="sl-page ingredient-page">
        {/* Page Header */}
        <div className="pageheader borderleft">
          <Breadcrumbs items={[
            { label: 'Home', to: '/' },
            { label: 'Inventory', to: '/inventory' },
            { label: 'Ingredients' }
          ]} />
          <div className="pageheading">Inventory & Ingredients</div>
          <div className="pagedescription">Add, track, and manage all your soap-making ingredients.</div>
        </div>

        <div className="ingredient-page-content borderleft">
          {/* Expiry Alerts */}
          {(() => {
            const today = new Date();
            const warningDays = 30;
            const expiringSoon = ingredients.filter(ing => {
              if (!ing.expiryDate) return false;
              const expiry = new Date(ing.expiryDate);
              const daysUntil = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
              return daysUntil <= warningDays && daysUntil > 0;
            });
            const expired = ingredients.filter(ing => {
              if (!ing.expiryDate) return false;
              const expiry = new Date(ing.expiryDate);
              return expiry < today;
            });
            
            if (expired.length === 0 && expiringSoon.length === 0) return null;
            
            return (
              <section className="mypadding borderbottom">
                {expired.length > 0 && (
                  <div className="ing-alert expired">
                    <div className="alert-title">Expired Ingredients ({expired.length})</div>
                    <div className="alert-tags">
                      {expired.map(ing => (
                        <span key={ing.firebaseId || ing.key} className="alert-tag">
                          {ing.name} - Expired {new Date(ing.expiryDate).toLocaleDateString()}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {expiringSoon.length > 0 && (
                  <div className="ing-alert expiring">
                    <div className="alert-title">Expiring Soon ({expiringSoon.length})</div>
                    <div className="alert-tags">
                      {expiringSoon.map(ing => {
                        const daysUntil = Math.ceil((new Date(ing.expiryDate) - today) / (1000 * 60 * 60 * 24));
                        return (
                          <span key={ing.firebaseId || ing.key} className="alert-tag">
                            {ing.name} - {daysUntil} days left
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </section>
            );
          })()}

          {/* Toolbar: Add button + Search + Filter + Sort */}
          <section className="mypadding borderbottom">
            <div className="ing-toolbar">
              <button
                className="ing-add-toggle"
                onClick={() => setShowAddForm(!showAddForm)}
              >
                {showAddForm ? 'Close Form' : '＋ Add Ingredient'}
              </button>
              <div className="ing-toolbar-filters">
                <div className="ing-search">
                  <input
                    type="text"
                    placeholder="Search ingredients..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button className="ing-search-clear" onClick={() => setSearchQuery('')}>✕</button>
                  )}
                </div>
                <select
                  className="ing-filter-select"
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                >
                  <option value="all">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
                  ))}
                </select>
                <select
                  className="ing-filter-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="name">Sort: Name</option>
                  <option value="stock">Sort: Stock (low first)</option>
                  <option value="expiry">Sort: Expiry (soonest)</option>
                  <option value="cost">Sort: Cost (high first)</option>
                </select>
              </div>
            </div>

            {/* Collapsible Add Form */}
            {showAddForm && (
              <div className="ing-add-form-section">
                <AddIngredient onAdd={(ing) => { handleAddIngredient(ing); setShowAddForm(false); }} />
              </div>
            )}
          </section>

          {/* Inventory display */}
          <section className="mypadding">
            <div className="ing-inventory-header">
              <h3>Inventory ({filteredIngredients.length}{filteredIngredients.length !== ingredients.length ? ` of ${ingredients.length}` : ''} items)</h3>
            </div>

            {ingredients.length === 0 ? (
              <div className="ing-empty">
                No ingredients added yet. Click "+ Add Ingredient" above to get started.
              </div>
            ) : filteredIngredients.length === 0 ? (
              <div className="ing-empty">
                No ingredients match your search. Try adjusting your filters.
              </div>
            ) : (
              <div>
                {Object.entries(filteredGrouped).map(([cat, list]) => {
                  const title = cat.charAt(0).toUpperCase() + cat.slice(1);
                  return (
                    <div key={cat} className="ing-category-group">
                      <div className="ing-category-title">{title} ({list.length})</div>
                      <div className="ing-cards-grid">
                        {list.map((ing) => {
                          if (!ing) return null;
                          const globalIdx = ingredients.findIndex((g) =>
                            g.firebaseId ? g.firebaseId === ing.firebaseId : g.key === ing.key && g.name === ing.name
                          );
                          const isEditing = editingIdx === globalIdx;
                          const lowStock = isLowStock(ing);
                          return (
                            <div key={ing.firebaseId || ing.key || ing.name} className={`ing-card${isEditing ? ' editing' : ''}${lowStock ? ' low-stock' : ''}`}>
                              <div className="ing-card-body">
                                <div className="ing-card-header">
                                  <div className="ing-card-name">
                                    {isEditing ? (
                                      <input
                                        type="text"
                                        value={editForm.name || ''}
                                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                      />
                                    ) : (
                                      <>
                                        {ing.name || ing.key || 'Unnamed'}
                                        {lowStock && <span className="ing-badge low-stock">Low Stock</span>}
                                      </>
                                    )}
                                  </div>
                                  <div className="ing-card-actions">
                                    {isEditing ? (
                                      <>
                                        <button className="ing-card-btn save" onClick={() => handleSaveEdit(globalIdx)}>Save</button>
                                        <button className="ing-card-btn cancel" onClick={handleCancelEdit}>Cancel</button>
                                      </>
                                    ) : (
                                      <>
                                        <button className="ing-card-btn edit" onClick={() => handleEdit(globalIdx)}>Edit</button>
                                        <button className="ing-card-btn delete" onClick={() => handleDelete(globalIdx)}>Delete</button>
                                      </>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Edit Form */}
                                {isEditing ? (
                                  <div className="ing-card-edit-form">
                                    <div className="edit-field">
                                      <strong>Stock:</strong>
                                      <input type="number" value={editForm.stock ?? ''} onChange={(e) => setEditForm({ ...editForm, stock: e.target.value === '' ? '' : Number(e.target.value) })} />
                                      <select value={editForm.unit || 'kg'} onChange={(event) => {
                                        try {
                                          const factor = convertQuantity(1, editForm.unit || 'kg', event.target.value, editForm.densityGPerMl);
                                          setEditForm({ ...editForm, unit: event.target.value, stock: Number(editForm.stock || 0) * factor, costPerUnit: Number(editForm.costPerUnit || 0) / factor });
                                        } catch (error) { alert(error.message); }
                                      }}>
                                        <option value="kg">kg</option><option value="g">g</option><option value="L">L</option><option value="ml">ml</option><option value="pcs">pcs</option>
                                      </select>
                                    </div>
                                    <div className="edit-field">
                                      <strong>Cost:</strong>
                                      <input type="number" value={editForm.costPerUnit ?? ''} onChange={(e) => setEditForm({ ...editForm, costPerUnit: e.target.value === '' ? '' : Number(e.target.value) })} />
                                      <span>per {editForm.unit || 'kg'}</span>
                                    </div>
                                    {['l', 'ml'].includes((editForm.unit || '').toLowerCase()) && <div className="edit-field"><strong>Density (g/ml):</strong><input type="number" min="0.001" step="any" value={editForm.densityGPerMl ?? ''} onChange={(event) => setEditForm({ ...editForm, densityGPerMl: event.target.value === '' ? null : Number(event.target.value) })} /></div>}
                                    <div className="edit-field">
                                      <strong>Expiry:</strong>
                                      <input type="date" value={editForm.expiryDate || ''} onChange={(e) => setEditForm({ ...editForm, expiryDate: e.target.value })} />
                                    </div>
                                    <div className="edit-field">
                                      <strong>Purchased:</strong>
                                      <input type="date" value={editForm.purchaseDate || ''} onChange={(e) => setEditForm({ ...editForm, purchaseDate: e.target.value })} />
                                    </div>
                                    <div className="edit-field">
                                      <strong>Supplier:</strong>
                                      <input type="text" value={editForm.supplier || ''} onChange={(e) => setEditForm({ ...editForm, supplier: e.target.value })} />
                                    </div>

                                    {/* Oil-specific fields */}
                                    {(editForm.category === 'oil' || editForm.type === 'oil') && (
                                      <>
                                        <div className="edit-field">
                                          <strong>SAP:</strong>
                                          <input type="number" step="0.001" value={editForm.sap ?? ''} onChange={(e) => setEditForm({ ...editForm, sap: e.target.value === '' ? '' : Number(e.target.value) })} />
                                        </div>
                                        <div className="edit-field">
                                          <strong>Max %:</strong>
                                          <input type="number" value={editForm.recommendedMaxPct ?? ''} onChange={(e) => setEditForm({ ...editForm, recommendedMaxPct: e.target.value === '' ? '' : Number(e.target.value) })} />
                                        </div>
                                        <div className="edit-field">
                                          <strong>Hardness:</strong>
                                          <select value={editForm.hardness || 'Medium'} onChange={(e) => setEditForm({ ...editForm, hardness: e.target.value })}>
                                            <option value="Low">Low</option><option value="Medium">Medium</option><option value="High">High</option>
                                          </select>
                                        </div>
                                      </>
                                    )}

                                    {/* Fragrance-specific fields */}
                                    {(editForm.category === 'fragrance') && (
                                      <>
                                        <div className="edit-field">
                                          <strong>Max Face %:</strong>
                                          <input type="number" value={editForm.maxFacePct ?? ''} onChange={(e) => setEditForm({ ...editForm, maxFacePct: e.target.value === '' ? '' : Number(e.target.value) })} />
                                        </div>
                                        <div className="edit-field">
                                          <strong>Max Body %:</strong>
                                          <input type="number" value={editForm.maxBodyPct ?? ''} onChange={(e) => setEditForm({ ...editForm, maxBodyPct: e.target.value === '' ? '' : Number(e.target.value) })} />
                                        </div>
                                      </>
                                    )}

                                    {/* Colour-specific fields */}
                                    {(editForm.category === 'colour') && (
                                      <div className="edit-field">
                                        <strong>Color:</strong>
                                        <input type="text" value={editForm.colour || ''} onChange={(e) => setEditForm({ ...editForm, colour: e.target.value })} />
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div className="ing-card-summary">
                                    {ing.stock !== undefined && <span>{ing.stock} {ing.unit}</span>}
                                    {ing.costPerUnit !== undefined && (
                                      <span>
                                        {formatMoney(ing.costPerUnit, ing.currencyCode || preferredCurrency, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/{ing.unit}
                                        {ing.currencyCode && ing.currencyCode !== preferredCurrency && (
                                          <span style={{ color: '#6b7280', marginLeft: 6 }}>
                                            (≈ {convertAndFormat(ing.costPerUnit, ing.currencyCode, preferredCurrency, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                                          </span>
                                        )}
                                      </span>
                                    )}
                                    {ing.sap && <span>SAP: {ing.sap}</span>}
                                    {ing.expiryDate && <span>Exp: {new Date(ing.expiryDate).toLocaleDateString()}</span>}
                                    {ing.supplier && <span>{ing.supplier}</span>}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      <Footer />
      </div>

    </>
  );
}
